import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL, ENDPOINTS } from '../config';

interface CurrencyContextType {
  currency: string;
  setCurrency: (currency: string) => void;
  exchangeRates: { [key: string]: number };
  convertPrice: (price: number) => number;
  toUSD: (price: number) => number;
  formatPrice: (price: number) => string;
  availableCurrencies: string[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

interface CurrencyRateMasterDto {
  toCurrencyName: string;
  currentRate: number;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    return localStorage.getItem('currency') || 'USD';
  });

  const setCurrency = (newCurrency: string) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('currency', newCurrency);
  };
  const [exchangeRates, setExchangeRates] = useState<{ [key: string]: number }>({
    USD: 1,
    EUR: 0.85,
    SAR: 3.75,
    AED: 3.67,
    SDG: 900
  });

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CURRENCY_RATES}`);
        if (response.ok) {
          const data: CurrencyRateMasterDto[] = await response.json();
          const newRates: { [key: string]: number } = { USD: 1 };
          console.log('Fetched currency rates:', data);
          data.forEach(rate => {
            // Map common currency names to codes if necessary, or use the name directly
            // For now, we assume the name in DB is the code or close enough
            // We also handle potential duplicate names by overwriting
            // We check if the name contains common codes
            let code = rate.toCurrencyName;
            if (rate.toCurrencyName.includes('USD')) code = 'USD';
            else if (rate.toCurrencyName.includes('EUR')) code = 'EUR';
            else if (rate.toCurrencyName.includes('SAR')) code = 'SAR';
            else if (rate.toCurrencyName.includes('AED')) code = 'AED';
            else if (rate.toCurrencyName.includes('SDG')) code = 'SDG';
            else if (rate.toCurrencyName.includes('GBP')) code = 'GBP';
            else if (rate.toCurrencyName.includes('CNY')) code = 'CNY';
            
            newRates[code] = rate.currentRate;
          });
          
          setExchangeRates(newRates);
        }
      } catch (error) {
        console.error('Failed to fetch currency rates:', error);
      }
    };

    fetchRates();
  }, []);

  const convertPrice = (price: number): number => {
    // Default to 1 if currency not found
    const rate = exchangeRates[currency] || 1;
    return price * rate;
  };
  
  const toUSD = (price: number): number => {
    const rate = exchangeRates[currency] || 1;
    if (!rate || rate === 0) return price;
    return price / rate;
  };

  const formatPrice = (price: number): string => {
    const convertedPrice = convertPrice(price);
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
      }).format(convertedPrice);
    } catch (e) {
      // Fallback if currency code is invalid
      return `${currency} ${convertedPrice.toFixed(2)}`;
    }
  };

  const availableCurrencies = Object.keys(exchangeRates);

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrency, 
      exchangeRates, 
      convertPrice, 
      toUSD,
      formatPrice,
      availableCurrencies
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
